import { z } from 'zod';
import { GAME_TYPES, TABLE_STATUSES, WAITLIST_STATUSES, USER_ROLES } from '../types';
import { containsBlockedWords, getProfanityErrorMessage } from '../utils/contentFilter';

// Room schemas
export const CreateRoomSchema = z.object({
  name: z.string().min(1, 'Room name is required').max(100, 'Room name too long'),
  address: z.string().min(1, 'Address is required').max(200, 'Address too long'),
  phone: z.string().min(1, 'Phone is required').max(20, 'Phone number too long'),
  waitlist_limit: z.number().int().min(1).max(100).optional(),
});

export const UpdateRoomSchema = CreateRoomSchema.partial();

// Table schemas
export const CreateTableSchema = z.object({
  name: z.string().min(1, 'Table name is required').max(50, 'Table name too long'),
  game_type: z.enum(GAME_TYPES as [string, ...string[]]),
  custom_game_type: z.string().max(50).optional(),
  stakes: z.string().min(1, 'Stakes are required').max(20, 'Stakes format too long'),
  number_of_tables: z.number().int().min(1).max(50, 'Invalid table count'),
  waitlist_limit: z.number().int().min(1).max(100).optional(),
});

export const UpdateTableSchema = z.object({
  name: z.string().min(1, 'Table name is required').max(50, 'Table name too long').optional(),
  game_type: z.enum(GAME_TYPES as [string, ...string[]]).optional(),
  custom_game_type: z.string().max(50).optional(),
  stakes: z.string().min(1, 'Stakes are required').max(20, 'Stakes format too long').optional(),
  number_of_tables: z.number().int().min(0).max(50, 'Invalid table count').optional(),
  status: z.enum(TABLE_STATUSES as [string, ...string[]]).optional(),
  waitlist_limit: z.number().int().min(1).max(100).optional(),
}).refine((data) => {
  // If status is Interest or Closed, number_of_tables should be 0
  if (data.status === 'Interest' || data.status === 'Closed') {
    return data.number_of_tables === undefined || data.number_of_tables === 0;
  }
  // For other statuses, number_of_tables should be at least 1
  if (data.status === 'Open' || data.status === 'Running') {
    return data.number_of_tables === undefined || data.number_of_tables >= 1;
  }
  return true;
}, {
  message: "Number of tables must be 0 for Interest/Closed status, or at least 1 for Open/Running status",
  path: ["number_of_tables"]
});

// Waitlist schemas
export const CreateWaitlistEntrySchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  phone: z.string().max(20, 'Phone number too long').optional(),
  user_id: z.string().optional(),
});

export const UpdateWaitlistEntrySchema = z.object({
  name: z.string().min(1).max(100).optional(),
  phone: z.string().max(20).optional().or(z.literal('')),
  status: z.enum(WAITLIST_STATUSES as [string, ...string[]]).optional(),
});

// Saved Player schemas (room-scoped contact storage)
export const CreateSavedPlayerSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  phone: z.string().max(20, 'Phone number too long').optional(),
});

export const UpdateSavedPlayerSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long').optional(),
  phone: z.string().max(20, 'Phone number too long').optional().or(z.literal('')),
});

// User schemas
export const CreateUserProfileSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  email: z.string().email('Invalid email format'),
  phone: z.string().max(20, 'Phone number too long').optional(),
  alias: z.string()
    .max(12, 'Alias must be 12 characters or less')
    .refine((val) => {
      if (!val || val.trim() === '') return true;
      return !containsBlockedWords(val);
    }, {
      message: getProfanityErrorMessage('Poker alias')
    })
    .optional(),
  role: z.enum(USER_ROLES as [string, ...string[]]),
  roomId: z.string().optional(),
  signup_source: z.enum(['web', 'mobile']).optional(),
});

export const UpdateUserProfileSchema = z.object({
  phone: z.string().max(20).optional(),
  alias: z.string()
    .max(12, 'Alias must be 12 characters or less')
    .refine((val) => {
      if (!val || val.trim() === '') return true; // Empty aliases allowed
      return !containsBlockedWords(val);
    }, {
      message: getProfanityErrorMessage('Poker alias')
    })
    .optional(),
});


// Auth schemas
export const LoginSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

export const SignupSchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name too long')
    .refine((val) => !containsBlockedWords(val), {
      message: getProfanityErrorMessage('Name')
    }),
  email: z.string().email('Invalid email format'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  phone: z.string().max(20, 'Phone number too long').optional(),
});

export const ForgotPasswordSchema = z.object({
  email: z.string().email('Invalid email format'),
});

// Type exports for use in components
export type LoginData = z.infer<typeof LoginSchema>;
export type SignupData = z.infer<typeof SignupSchema>;
export type ForgotPasswordData = z.infer<typeof ForgotPasswordSchema>;
